import { useEffect, useRef } from 'react';

import { composeDictation } from '../utils/composeDictation';

import type { DictationController } from './useDictation';

/**
 * Folds a dictation session into a controlled string value. The value at the
 * moment dictation starts is the base; every turn update re-emits
 * base + spoken text, and the end of the session commits it.
 */
export function useDictatedValue({
  value,
  onValueChange,
  controller,
  maxLength,
}: {
  value: string;
  onValueChange: (value: string) => void;
  controller: DictationController;
  /** The field's character cap; spoken text is cut to fit, as typing would be. */
  maxLength?: number;
}): void {
  const base = useRef<string | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const emit = useRef(onValueChange);
  emit.current = onValueChange;
  const { status, turns, consumeTurns } = controller;

  useEffect(() => {
    if (status === 'starting') base.current = valueRef.current;
  }, [status]);

  useEffect(() => {
    if (base.current === null || turns.length === 0) return;
    const composed = composeDictation(base.current, turns);
    const next = maxLength !== undefined ? composed.slice(0, maxLength) : composed;
    if (next !== valueRef.current) emit.current(next);
  }, [turns, maxLength]);

  useEffect(() => {
    if (status !== 'idle' || base.current === null) return;
    base.current = null;
    consumeTurns();
  }, [status, consumeTurns]);
}
